import { motion } from 'framer-motion';
import { Mic, ShoppingBag, Store } from 'lucide-react';
import linenShirtImg from '@/assets/onboarding/linen-shirt.jpg';
import chinoShortsImg from '@/assets/onboarding/chino-shorts.jpg';
import sneakersImg from '@/assets/onboarding/sneakers.jpg';
import sambaImg from '@/assets/onboarding/samba.jpg';

const cardVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.97 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: 0.2 + i * 0.12,
      duration: 0.45,
      type: 'spring' as const,
      damping: 22,
      stiffness: 220,
    },
  }),
};

const outfitItems = [
  { img: linenShirtImg, name: 'Floral Midi Skirt', store: 'ZARA', price: '$59' },
  { img: chinoShortsImg, name: 'Straw Crossbody', store: '& Other Stories', price: '$45' },
  { img: sneakersImg, name: 'Air Max 90', store: 'Nike', price: '$97' },
  { img: sambaImg, name: 'Samba OG', store: 'Adidas', price: '$89' },
];

export function PersonalStylistMockup({ isActive = true }: { isActive?: boolean }) {
  return (
    <div className="absolute inset-0 bg-gradient-to-b from-muted to-background flex items-center justify-center px-5 pt-2 pb-4">
      <div className="w-full max-w-[290px] space-y-1.5">
        {/* User message */}
        <motion.div
          custom={0}
          initial="hidden"
          animate={isActive ? 'visible' : 'hidden'}
          variants={cardVariants}
          className="flex justify-end"
        >
          <div className="bg-primary text-primary-foreground rounded-2xl rounded-br-md px-3.5 py-2.5 max-w-[80%]">
            <p className="text-xs text-primary-foreground">Build me a summer outfit ☀️</p>
          </div>
        </motion.div>

        {/* AI reply with outfit grid */}
        <motion.div
          custom={1}
          initial="hidden"
          animate={isActive ? 'visible' : 'hidden'}
          variants={cardVariants}
          className="flex justify-start"
        >
          <div className="bg-card rounded-2xl rounded-bl-md px-3 py-3 max-w-[95%] shadow-sm border border-border/50 space-y-2.5">
            <p className="text-xs text-heading">Here's a look from 4 stores! 🛍️</p>

            {/* 2x2 product grid */}
            <div className="grid grid-cols-2 gap-1.5">
              {outfitItems.map((item, i) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isActive ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
                  transition={{ delay: 0.5 + i * 0.1, type: 'spring' }}
                  className="bg-muted rounded-lg overflow-hidden"
                >
                  <div className="w-full aspect-[4/3] overflow-hidden">
                    <img src={item.img} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="px-1.5 py-1 text-center">
                    <p className="text-[9px] font-medium text-heading truncate">{item.name}</p>
                    <p className="text-[8px] text-muted-foreground">{item.store} · {item.price}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <p className="text-[10px] text-muted-foreground">Total: $290 ✨</p>
              <div className="flex items-center gap-1">
                <Store className="w-2.5 h-2.5 text-primary" />
                <span className="text-[9px] font-medium text-primary">4 stores</span>
              </div>
            </div>
          </div>
        </motion.div>


        {/* Voice waveform */}
        <motion.div
          custom={3}
          initial="hidden"
          animate={isActive ? 'visible' : 'hidden'}
          variants={cardVariants}
          className="bg-card rounded-2xl px-3 py-2 border border-border/50 shadow-sm flex items-center gap-2.5"
        >
          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Mic className="w-3 h-3 text-primary" />
          </div>
          <div className="flex items-end gap-[2px] h-4 flex-1">
            {[3, 8, 14, 6, 12, 16, 5, 10, 14, 7, 4, 11, 15, 8, 6, 13, 9, 5, 11, 7, 14, 4, 10, 16, 6, 12, 8, 3, 9, 15, 5, 11, 7, 13, 4, 10].map((h, i) => (
              <motion.div
                key={i}
                className="rounded-full bg-primary/40 flex-1 min-w-[1.5px]"
                animate={{ 
                  height: [2, Math.min(h, 16), Math.min(h * 0.4, 8), Math.min(h * 0.9, 14), 2, Math.min(h * 0.7, 12), Math.min(h, 16)],
                }}
                transition={{ 
                  delay: 0.9 + i * 0.03,
                  duration: 1.8 + (i % 3) * 0.3,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />
            ))}
          </div>
          <span className="text-[9px] text-muted-foreground shrink-0">Or speak</span>
        </motion.div>
      </div>
    </div>
  );
}
